// src/components/groups/JoinGroupLink.jsx
// The shared join link page, #/join/<token> (Roadmap #54, Invitations Build 2 §5, Phase 3). The
// link reveals the group's name and place only (get_join_link_preview, open to anyone holding it).
// Signed out → sign in or create an account (the link rides the confirmation email, §1); signed in
// → one Join press, no admin approval, public and private groups alike (Michael's ruling, 1 Oct).
// Where it lands (addendum 4 Q1a): a private group's link always opens its Introduction page
// (LandingPageGroup in "arrived with an invitation" mode); a public group's only when the group
// has written an Introduction; otherwise this page.

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { groupService } from '../../data/dataService';
import StandardButton from '../core/StandardButton';
import InvitationPageShell, { InvitationGroupBlock, InvitationActions, InvitationText } from './InvitationPageShell';
import InvitationOutcome, { goHome } from './InvitationOutcome';
import LandingPageGroup from './LandingPageGroup';

const JoinGroupLink = ({ joinToken, currentUser, onShowLogin }) => {
  const { t } = useTranslation('groups');
  const [preview, setPreview] = useState(null);
  const [joining, setJoining] = useState(false);
  const [result, setResult] = useState(null);
  const [intro, setIntro] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setResult(null);
    setIntro(null);
    // A cut-off or mangled link arrives with no token: say so without asking the server.
    if (!joinToken) { setPreview({ state: 'not_found' }); return undefined; }
    groupService.getJoinLinkPreview(joinToken).then(async p => {
      // What the link lets its holder see; only asked for a live link.
      const i = p?.state === 'valid' && p.groupId ? await groupService.getGroupIntroduction(p.groupId, joinToken) : null;
      if (cancelled) return;
      setIntro(i);
      setPreview(p);
    });
    return () => { cancelled = true; };
  }, [joinToken]);

  const handleJoin = async () => {
    if (!currentUser?.id) {
      onShowLogin?.(t('join_sign_in_prompt'));
      return;
    }
    setJoining(true);
    const res = await groupService.acceptGroupJoinLink(joinToken);
    setJoining(false);
    setResult({ ...res, groupName: res.groupName || preview?.groupName || null });
  };

  if (!preview) return <InvitationPageShell loading />;

  if (result) {
    return (
      <InvitationOutcome
        kind="link"
        code={result.outcome === 'error' ? 'failed' : result.outcome}
        groupId={result.groupId || preview.groupId}
        groupName={result.groupName}
      />
    );
  }

  if (preview.state !== 'valid') {
    return <InvitationOutcome kind="link" code={preview.state === 'error' ? 'failed' : preview.state} />;
  }

  if (intro?.found && (intro.accessType !== 'public' || intro.hasIntroduction)) {
    return (
      <LandingPageGroup
        group={{ id: intro.id, name: intro.name }}
        currentUser={currentUser}
        invitation={{ kind: 'link', token: joinToken }}
        onShowLogin={onShowLogin}
      />
    );
  }

  return (
    <InvitationPageShell title={t('join_link_title', { group: preview.groupName })}>
      <InvitationGroupBlock name={preview.groupName} place={preview.place} />
      <InvitationText>{t('join_link_intro')}</InvitationText>
      <InvitationActions>
        {currentUser?.id ? (
          <StandardButton
            variant="action"
            iconName="Users"
            label={joining ? t('join_joining') : t('join_button')}
            onClick={handleJoin}
            disabled={joining}
          />
        ) : (
          <>
            <StandardButton
              variant="action"
              iconName="LogIn"
              label={t('join_sign_in')}
              onClick={() => onShowLogin?.(t('join_sign_in_prompt'))}
            />
            <StandardButton
              variant="ghost"
              iconName="User"
              label={t('join_register')}
              onClick={() => onShowLogin?.(t('join_sign_in_prompt'), 'register')}
            />
          </>
        )}
        <StandardButton variant="ghost" label={t('join_not_now')} onClick={goHome} />
      </InvitationActions>
    </InvitationPageShell>
  );
};

export default JoinGroupLink;
